import React from 'react';
import { DSBadge, DSCard, DSCardFooterButton, DSIcon } from '../../design-system/primitives';
import { relativeDay, formatDay } from './helpers';
import { isEventCancelled } from '../../utils/eventHelpers';

// Everything the club is waiting on this member for, in one place: events they
// host or said they are going to, tasks they claimed, and machine issues
// assigned to them. Each of these already lives on its own page; what did not
// exist was a single answer to "is anything waiting on me".
//
// Rows link out to the page that owns the thing rather than duplicating its
// controls here. Releasing a task or closing a ticket stays where it was.

const ACTIONABLE_TASK_STATUSES = ['open', 'in_progress', 'overdue'];

// Every group renders whether or not it has rows. A group that disappears when it
// empties makes the tab a different shape every visit, and the member cannot tell
// "you have no machine issues" from "machine issues are not a thing here". An
// empty group keeps its header, its count, and its link out, and says plainly
// that there is nothing in it.
function Group({ title, rows, emptyText, linkLabel, onLink, onOpenRow }) {
  return (
    <DSCard
      frame="bars"
      density="compact"
      headerTone="strong"
      flush
      title={title}
      actions={<DSBadge variant="neutral" size="sm">{rows.length}</DSBadge>}
      footer={<DSCardFooterButton onClick={onLink}>{linkLabel}</DSCardFooterButton>}
    >
      {rows.length === 0 ? (
        <p className="mm-assign-empty">{emptyText}</p>
      ) : rows.map((row) => (
        <button key={row.key} type="button" className="mm-assign-row" onClick={() => onOpenRow(row)}>
          <span className="mm-assign-row__main">
            <span className="mm-assign-row__title">{row.title}</span>
            <span className="mm-assign-row__meta">{row.meta}</span>
          </span>
          {row.tag && (
            <DSBadge
              variant={row.cancelled ? 'error' : row.urgent ? 'warning' : 'neutral'}
              size="sm"
              uppercase={Boolean(row.cancelled)}
            >
              {row.tag}
            </DSBadge>
          )}
          <DSIcon name="chevronRight" size="sm" tone="muted" />
        </button>
      ))}
    </DSCard>
  );
}

// A cancelled night stays on the list so the member sees the news, but its tag says
// Cancelled instead of counting down to a night that is not happening.
const whenTag = (event, now) => (isEventCancelled(event)
  ? { tag: 'Cancelled', cancelled: true, urgent: false }
  : {
    tag: relativeDay(event.event_date),
    urgent: new Date(event.event_date).getTime() - now < 3 * 86400000
  });

export function buildAssignedGroups(assigned, tier = 0) {
  const now = Date.now();

  const hostedIds = new Set((assigned.hosted || []).map((event) => event.id));

  const hosted = (assigned.hosted || [])
    .filter((event) => new Date(event.event_date).getTime() >= now)
    .map((event) => ({
      key: `hosted-${event.id}`,
      to: `/events/${event.id}`,
      title: event.title,
      meta: [
        'Hosting',
        formatDay(event.event_date),
        `${event.rsvp_count || 0} going`
      ].filter(Boolean).join(' · '),
      ...whenTag(event, now)
    }));

  // An event you host and also RSVP'd to is one row, the hosting one.
  const going = (assigned.rsvps || [])
    .filter((rsvp) => !hostedIds.has(rsvp.event_id))
    .filter((rsvp) => new Date(rsvp.event_date).getTime() >= now)
    .filter((rsvp) => String(rsvp.status).toLowerCase() !== 'not_going')
    .map((rsvp) => ({
      key: `rsvp-${rsvp.event_id}`,
      to: `/events/${rsvp.event_id}`,
      title: rsvp.title,
      meta: [
        String(rsvp.status).toLowerCase() === 'going' ? 'Going' : 'Interested',
        formatDay(rsvp.event_date)
      ].filter(Boolean).join(' · '),
      ...whenTag(rsvp, now)
    }));

  const tasks = (assigned.tasks || [])
    .filter((task) => ACTIONABLE_TASK_STATUSES.includes(task.status))
    .map((task) => {
      const overdue = task.due_date && new Date(task.due_date).getTime() < now;
      return {
        key: `task-${task.id}`,
        to: `/tasks/${task.id}`,
        title: task.title,
        meta: [
          task.category_name,
          task.point_value ? `${task.point_value} points` : null,
          task.bounty_amount ? `$${parseFloat(task.bounty_amount).toFixed(2)} bounty` : null
        ].filter(Boolean).join(' · ') || 'Claimed by you',
        tag: task.due_date ? (overdue ? 'Overdue' : `Due ${formatDay(task.due_date)}`) : 'No due date',
        urgent: overdue || task.status === 'overdue'
      };
    });

  const issues = (assigned.issues || []).map((issue) => ({
    key: `issue-${issue.id}`,
    to: `/machine-issues/${issue.id}`,
    title: `${issue.machine_name}, ${issue.description}`,
    meta: [`Reported ${formatDay(issue.created_at)}`, issue.reporter_name ? `by ${issue.reporter_name}` : null]
      .filter(Boolean).join(' '),
    tag: issue.impact === 'game_down' ? 'Game down' : issue.impact === 'tournament_risk' ? 'Tournament risk' : 'Playable',
    urgent: issue.impact === 'game_down'
  }));

  return [
    {
      title: 'Your events',
      rows: [...hosted, ...going],
      emptyText: 'Nothing on your calendar. Events you host or RSVP to show up here.',
      linkLabel: 'View all events',
      to: '/events'
    },
    {
      title: 'Tasks you claimed',
      rows: tasks,
      emptyText: 'No open tasks. Anything you claim from the task board lands here.',
      linkLabel: 'View the task board',
      to: '/tasks'
    },
    {
      title: 'Machine issues assigned to you',
      rows: issues,
      // A Tier 0 member is never assigned an issue, so the empty state says why
      // rather than leaving them to wonder what they did wrong.
      emptyText: tier > 0
        ? 'None assigned to you. Issues a tech hands you show up here until they are closed.'
        : 'Machine issues are assigned to Tier 1 and above. Your tier is shown under Profile.',
      linkLabel: 'View all machine issues',
      to: '/machines/issues'
    }
  ];
}

function AssignedTab({ groups, loading, onNavigate }) {
  if (loading) {
    return <p className="mm-empty">Checking what is waiting on you...</p>;
  }

  return (
    <>
      {groups.map((group) => (
        <Group
          key={group.title}
          title={group.title}
          rows={group.rows}
          emptyText={group.emptyText}
          linkLabel={group.linkLabel}
          onLink={() => onNavigate(group.to)}
          onOpenRow={(row) => onNavigate(row.to)}
        />
      ))}
    </>
  );
}

export default AssignedTab;
